import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTransition, animated, useSpringRef } from '@react-spring/web';
import {
  CheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { TWButton } from '../../components/ui';
import { useDispatch } from 'react-redux';
import {
  loginSuccess,
  setRole,
  setRolePermissions,
} from '../../store/slices/authSlice';
import userApi from '../../services/userApi';
import tokenService from '../../utils/tokenService';
import LogoBlack from '../../assets/logo-black.png';
import LogoWhite from '../../assets/white-logo-brand.svg';
import RegisterImage from '../../assets/register-image.png';
import Notification from '../../components/notification/notification';
import Account from './steps/account';
import Restaurant from './steps/restaurant';
import Owner from './steps/owner';
import useReferralInvite from './useReferralInvite';
import ReferralInviteBanner from './ReferralInviteBanner';
import Turnstile from '../../components/turnstile/turnstile';
import moment from 'moment';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const STEP_META = {
  1: {
    eyebrow: null,
    title: 'Create your account',
    subtitle: 'Just your email and a password to get started.',
  },
  2: {
    eyebrow: null,
    title: 'Tell us about your restaurant',
    subtitle: 'A few details about your restaurant.',
  },
  3: {
    eyebrow: null,
    title: 'Almost there',
    subtitle: 'Who’s the main contact for your account?',
  },
};

const Register = () => {
  let navigate = useNavigate();

  const dispatch = useDispatch();
  const { referralcode, referrer } = useReferralInvite();
  const [errors, setErrors] = useState({
    email: false,
    password: false,
    passwordlength: false,
    terms: false,
    companyname: false,
    storeaddress: false,
    subscriptionid: false,
    responsiblename: false,
    responsiblephone: false,
    showNotf: false,
    message: '',
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isNext, setIsNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState({
    email: '',
    password: '',
    terms: false,
    companyname: '',
    storeaddress: '',
    postalcode: '',
    timezone: moment().utcOffset() / 60,
    timezoneid:
      Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
    state: '',
    city: '',
    apartment: '',
    subscriptionid: null,
    responsiblename: '',
    responsiblephone: '',
    longitude: 0,
    latitude: 0,
  });
  const [steps, setSteps] = useState([
    { id: 1, name: 'Account', status: 'current' },
    { id: 2, name: 'Restaurant', status: 'upcoming' },
    { id: 3, name: 'Owner', status: 'upcoming' },
  ]);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const transRef = useSpringRef();
  const transitions = useTransition(currentStep, {
    ref: transRef,
    keys: null,
    from: {
      opacity: 0,
      transform: isNext ? 'translate3d(8%,0,0)' : 'translate3d(-8%,0,0)',
    },
    enter: { opacity: 1, transform: 'translate3d(0%,0,0)' },
    leave: {
      opacity: 0,
      transform: isNext ? 'translate3d(-8%,0,0)' : 'translate3d(8%,0,0)',
    },
    config: { tension: 280, friction: 28 },
  });

  useEffect(() => {
    transRef.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const onChange = (value, key) => {
    if (value && typeof value === 'object') {
      setUserData((prev) => ({
        ...prev,
        ...(value.companyname !== undefined
          ? { companyname: value.companyname }
          : {}),
        storeaddress: value.street ?? '',
        city: value.city ?? '',
        state: value.state ?? '',
        postalcode: value.zipCode ?? '',
        apartment: value.apartment ?? '',
        longitude: value.longitude ?? 0,
        latitude: value.latitude ?? 0,
      }));
    } else {
      setUserData((prev) => ({ ...prev, [key]: value }));
    }
  };

  const stepComponents = [
    <Account
      errors={errors}
      onChange={onChange}
      email={userData.email}
      password={userData.password}
      terms={userData.terms}
    />,
    <Restaurant
      errors={errors}
      onChange={onChange}
      companyname={userData.companyname}
      storeaddress={userData.storeaddress}
      city={userData.city}
      state={userData.state}
      apartment={userData.apartment}
      postalcode={userData.postalcode}
      subscriptionid={userData.subscriptionid}
    />,
    <Owner
      errors={errors}
      onChange={onChange}
      responsiblename={userData.responsiblename}
      responsiblephone={userData.responsiblephone}
    />,
  ];

  const isValidEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const stepValidation = (step) => {
    if (step === 1) {
      const { email, password, terms } = userData;
      if (
        !email ||
        !password ||
        !terms ||
        password.length < 8 ||
        !isValidEmail(email)
      ) {
        setErrors({
          ...errors,
          email: !email || !isValidEmail(email),
          password: !password,
          terms: !terms,
          passwordlength: password.length < 8,
        });
        return false;
      }
    } else if (step === 2) {
      const { companyname, storeaddress } = userData;
      if (!companyname || !storeaddress || !storeaddress.trim()) {
        setErrors({
          ...errors,
          companyname: !companyname,
          storeaddress: !storeaddress || !storeaddress.trim(),
        });
        return false;
      }
    } else if (step === 3) {
      const { responsiblename, responsiblephone } = userData;
      if (
        !responsiblename ||
        !responsiblephone ||
        responsiblephone.replace(/[()\s-]/g, '').length < 10
      ) {
        setErrors({
          ...errors,
          responsiblename: !responsiblename,
          responsiblephone:
            !responsiblephone ||
            responsiblephone.replace(/[()\s-]/g, '').length < 10,
        });
        return false;
      }
    }
    return true;
  };

  const nextStep = (newStep) => {
    setSteps((prev) => {
      return prev.map((step) => {
        if (step.id === newStep) {
          return { ...step, status: 'current' };
        } else if (step.id === newStep - 1) {
          return { ...step, status: 'complete' };
        } else {
          return step;
        }
      });
    });
  };

  const backStep = (newStep) => {
    setSteps((prev) => {
      return prev.map((step) => {
        if (step.id === newStep) {
          return { ...step, status: 'current' };
        } else if (step.id === newStep + 1) {
          return { ...step, status: 'upcoming' };
        } else {
          return step;
        }
      });
    });
  };

  const handleClick = async (direction) => {
    let newStep = currentStep;

    if (direction === 'next') {
      setIsNext(true);
      if (stepValidation(currentStep)) {
        if (newStep === 3) {
          setLoading(true);

          try {
            userData.fullname = userData.responsiblename;
            userData.email = userData.email.trim();

            const registeredUser = await userApi.register(
              {
                ...userData,
                referralcode,
              },
              turnstileToken
            );
            if (registeredUser.data.data) {
              tokenService.updateLocalAccessToken(registeredUser.data.data);
              const userDetail = await userApi.getUserDetail();
              if (userDetail.status === 200) {
                let rolepermissions = rolePermissions(
                  userDetail.data.data.userroles[0]?.role?.rolepermissions,
                );
                tokenService.setRole([150]);
                dispatch(loginSuccess(userDetail.data.data));
                dispatch(setRole([150]));
                dispatch(setRolePermissions(rolepermissions));
                navigate('/dashboard');
              } else {
                turnstileRef.current?.reset();
              }
            } else {
              turnstileRef.current?.reset();
            }
          } catch (error) {
            turnstileRef.current?.reset();
            setErrors({
              ...errors,
              showNotf: true,
              message: error.response.data,
            });
          } finally {
            setLoading(false);
          }
        }
        newStep++;
        nextStep(newStep);
      }
    } else {
      setIsNext(false);
      setErrors({
        email: false,
        password: false,
        passwordlength: false,
        terms: false,
        companyname: false,
        storeaddress: false,
        responsiblename: false,
        responsiblephone: false,
        showNotf: false,
        message: '',
      });
      newStep--;
      backStep(newStep);
    }

    newStep > 0 && newStep <= steps.length && setCurrentStep(newStep);
  };

  const rolePermissions = (permissions) => {
    let rolepermissions = [];
    permissions.forEach((permission) => {
      rolepermissions.push(permission.permission.code);
    });
    return rolepermissions;
  };

  const progress = ((currentStep - 1) / (steps.length - 1)) * 100;
  const meta = STEP_META[currentStep] || STEP_META[1];

  return (
    <div className="relative flex min-h-screen flex-col bg-white dark:bg-slate-900 lg:flex-row">
      {/* LEFT — Photo + subtle bottom shadow + logo */}
      <aside className="relative hidden lg:block lg:w-1/2 lg:overflow-hidden">
        <img
          src={RegisterImage}
          alt="thisway self-order kiosk"
          className="absolute inset-0 h-full w-full object-cover"
        />
        {/* Bottom-only shadow — soft fade upward */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
        {/* Light top fade for logo readability */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/35 to-transparent" />

        <Link
          to="https://thisway.co"
          className="absolute left-10 top-10 z-10 inline-flex xl:left-14 xl:top-14"
        >
          <img
            src={LogoWhite}
            alt="thisway"
            className="h-8 w-auto drop-shadow-2xl"
          />
        </Link>
      </aside>

      {/* RIGHT — Form */}
      <main className="relative flex w-full flex-1 flex-col lg:w-1/2">
        {/* Top bar — mobile shows logo, desktop has it on the left panel */}
        <header className="flex items-center justify-between px-5 py-3 sm:px-8 sm:py-5 lg:px-12">
          <Link to="https://thisway.co" className="inline-flex lg:invisible">
            <img src={LogoBlack} alt="thisway" className="h-6 w-auto dark:hidden sm:h-7" />
            <img
              src={LogoWhite}
              alt="thisway"
              className="hidden h-6 w-auto dark:block sm:h-7"
            />
          </Link>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            Have an account?{' '}
            <Link
              to="/"
              className="font-semibold text-blue-600 hover:text-blue-700 dark:text-sky-400 dark:hover:text-sky-300"
            >
              Sign in
            </Link>
          </div>
        </header>

        <div className="flex flex-1 flex-col justify-center px-5 pb-4 sm:px-8 sm:pb-6 lg:px-12 xl:px-20">
          <div className="mx-auto w-full max-w-xl">
            <ReferralInviteBanner referrer={referrer} />
            {/* Stepper — hidden on step 1 to reduce friction */}
            {currentStep > 1 && (
              <nav aria-label="Progress" className="mt-1 mb-5 sm:mt-4 sm:mb-8">
                <div className="relative flex items-center justify-between">
                  {/* Track */}
                  <div
                    className="absolute left-0 right-0 top-3.5 h-[2px] bg-slate-200 dark:bg-slate-700 sm:top-5"
                    aria-hidden
                  />
                  {/* Fill */}
                  <div
                    className="absolute left-0 top-3.5 h-[2px] bg-blue-600 transition-all duration-500 ease-out dark:bg-sky-400 sm:top-5"
                    style={{ width: `${progress}%` }}
                    aria-hidden
                  />

                  {steps.map((step) => {
                    const isComplete = step.status === 'complete';
                    const isCurrent = step.status === 'current';
                    return (
                      <div
                        key={step.id}
                        className="relative z-10 flex flex-col items-center gap-1.5 sm:gap-2"
                      >
                        <span
                          className={classNames(
                            'flex h-7 w-7 items-center justify-center rounded-full text-[0.7rem] font-bold transition-all duration-300 sm:h-10 sm:w-10 sm:text-sm',
                            isComplete &&
                              'bg-blue-600 text-white shadow-md shadow-blue-600/30 dark:bg-sky-400 dark:text-slate-900 dark:shadow-sky-400/20',
                            isCurrent &&
                              'bg-white text-blue-600 ring-2 ring-blue-600 ring-offset-2 ring-offset-white dark:bg-slate-900 dark:text-sky-400 dark:ring-sky-400 dark:ring-offset-slate-900',
                            !isComplete &&
                              !isCurrent &&
                              'bg-slate-100 text-slate-400 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:ring-slate-700',
                          )}
                        >
                          {isComplete ? (
                            <CheckIcon className="h-3.5 w-3.5 sm:h-5 sm:w-5" strokeWidth={3} />
                          ) : (
                            step.id
                          )}
                        </span>
                        <span
                          className={classNames(
                            'text-[0.65rem] font-semibold tracking-wide sm:text-xs',
                            isCurrent || isComplete
                              ? 'text-slate-900 dark:text-slate-100'
                              : 'text-slate-400 dark:text-slate-500',
                          )}
                        >
                          {step.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </nav>
            )}

            {/* Heading */}
            <div className={classNames(currentStep > 1 ? 'mb-5 sm:mb-8' : 'mt-2 mb-5 sm:mt-10 sm:mb-8')}>
              {meta.eyebrow && (
                <p className="mb-1.5 text-[0.65rem] font-bold uppercase tracking-[0.2em] text-blue-600 dark:text-sky-400 sm:mb-2 sm:text-[0.7rem]">
                  {meta.eyebrow}
                </p>
              )}
              <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white sm:text-4xl">
                {meta.title}
              </h2>
              <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400 sm:mt-2 sm:text-base">
                {meta.subtitle}
              </p>
            </div>

            {/* Animated step container — grid stacking lets content set its own height */}
            <div className="grid grid-cols-1 overflow-x-hidden">
              {transitions((style, item) => (
                <animated.div
                  style={style}
                  className="col-start-1 row-start-1 w-full"
                >
                  {stepComponents[item - 1]}
                </animated.div>
              ))}
            </div>

            {currentStep === 3 && (
              <div className="mt-4">
                <Turnstile ref={turnstileRef} onToken={setTurnstileToken} />
              </div>
            )}

            {/* Navigation */}
            <div className="mt-5 flex items-center justify-between gap-3 border-t border-slate-100 pt-4 dark:border-slate-800 sm:mt-8 sm:pt-6">
              {currentStep > 1 ? (
                <TWButton
                  variant="ghost"
                  size="lg"
                  icon={ArrowLeftIcon}
                  onClick={() => handleClick('back')}
                  disabled={loading}
                  label="Back"
                />
              ) : (
                <span className="text-xs text-slate-400 dark:text-slate-500">
                  Should only take a minute.
                </span>
              )}
              <TWButton
                variant="primary"
                size="lg"
                iconRight={
                  currentStep === steps.length ? undefined : ArrowRightIcon
                }
                label={
                  currentStep === steps.length
                    ? 'Create account'
                    : 'Continue'
                }
                onClick={() => handleClick('next')}
                disabled={loading}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </main>

      <Notification
        display={errors.showNotf}
        type="error"
        message={errors.message}
        onClose={() => setErrors((prev) => ({ ...prev, showNotf: false }))}
      />
    </div>
  );
};

export default Register;
